/**
 * Cycle Service
 * Handles all OKR cycle-related API calls
 */

import apiClient from './api';
import type { ApiResponse } from '../types';

export interface OKRCycle {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  type: 'quarterly' | 'monthly' | 'annual' | 'custom';
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'active' | 'closed';
  autoGenerated: boolean;
  parentCycleId?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    okrs: number;
  };
}

export interface CreateCycleRequest {
  name: string;
  description?: string;
  type: 'quarterly' | 'monthly' | 'annual' | 'custom';
  startDate: string;
  endDate: string;
  parentCycleId?: string;
}

export interface UpdateCycleRequest {
  name?: string;
  description?: string;
  type?: 'quarterly' | 'monthly' | 'annual' | 'custom';
  startDate?: string;
  endDate?: string;
}

export const cycleService = {
  /**
   * Get all cycles
   */
  getCycles: async (): Promise<OKRCycle[]> => {
    const response = await apiClient.get<ApiResponse<{ cycles: OKRCycle[] }>>('/cycles');
    if (response.data.success && response.data.data) {
      return response.data.data.cycles;
    }
    throw new Error(response.data.message || 'Failed to fetch cycles');
  },

  /**
   * Get cycle by ID
   */
  getCycleById: async (id: string): Promise<OKRCycle> => {
    const response = await apiClient.get<ApiResponse<{ cycle: OKRCycle }>>(`/cycles/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data.cycle;
    }
    throw new Error(response.data.message || 'Failed to fetch cycle');
  },

  /**
   * Get active cycle
   */
  getActiveCycle: async (): Promise<OKRCycle | null> => {
    const response = await apiClient.get<ApiResponse<{ cycle: OKRCycle | null }>>(
      '/cycles/active'
    );
    if (response.data.success && response.data.data) {
      return response.data.data.cycle;
    }
    throw new Error(response.data.message || 'Failed to fetch active cycle');
  },

  /**
   * Create a new cycle
   */
  createCycle: async (data: CreateCycleRequest): Promise<OKRCycle> => {
    const response = await apiClient.post<ApiResponse<{ cycle: OKRCycle }>>('/cycles', data);
    if (response.data.success && response.data.data) {
      return response.data.data.cycle;
    }
    throw new Error(response.data.message || 'Failed to create cycle');
  },

  /**
   * Update a cycle
   */
  updateCycle: async (id: string, data: UpdateCycleRequest): Promise<OKRCycle> => {
    const response = await apiClient.patch<ApiResponse<{ cycle: OKRCycle }>>(
      `/cycles/${id}`,
      data
    );
    if (response.data.success && response.data.data) {
      return response.data.data.cycle;
    }
    throw new Error(response.data.message || 'Failed to update cycle');
  },

  /**
   * Close a cycle
   */
  closeCycle: async (id: string): Promise<OKRCycle> => {
    const response = await apiClient.post<ApiResponse<{ cycle: OKRCycle }>>(
      `/cycles/${id}/close`
    );
    if (response.data.success && response.data.data) {
      return response.data.data.cycle;
    }
    throw new Error(response.data.message || 'Failed to close cycle');
  },

  /**
   * Activate a cycle
   */
  activateCycle: async (id: string): Promise<OKRCycle> => {
    const response = await apiClient.post<ApiResponse<{ cycle: OKRCycle }>>(
      `/cycles/${id}/activate`
    );
    if (response.data.success && response.data.data) {
      return response.data.data.cycle;
    }
    throw new Error(response.data.message || 'Failed to activate cycle');
  },

  /**
   * Delete a cycle
   */
  deleteCycle: async (id: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse>(`/cycles/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete cycle');
    }
  },
};
